import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import ProcessSection from '../components/ProcessSection';
import MarqueeStrip from '../components/MarqueeStrip';
import { services } from '../data/services';

const Home = () => {
  // Only show first 6 services on home
  const featuredServices = services.slice(0, 6);

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-24 md:pt-32"
      >
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Text Content */}
            <div className="lg:col-span-7">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="eyebrow mb-6 block"
              >
                Services
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-huge font-['DM_Sans'] mb-8"
              >
                Design and make signs that get noticed.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10"
              >
                From sign boards and internal signage to flex printing, road signs, LED displays, and more.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <Link
                  to="/quote"
                  data-testid="hero-cta-primary"
                  className="btn-primary"
                >
                  Get Quick Quote
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="btn-secondary"
                >
                  View Services
                </Link>
              </motion.div>
            </div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="relative aspect-square">
                {/* Abstract grid visual */}
                <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-2">
                  {[...Array(9)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 + i * 0.1 }}
                      className={`rounded-sm ${
                        i % 3 === 0
                          ? 'bg-primary'
                          : i % 2 === 0
                          ? 'bg-accent'
                          : 'bg-secondary'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 hidden md:block"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              Scroll
            </span>
            <div className="w-px h-12 bg-border relative overflow-hidden">
              <motion.div
                className="absolute top-0 left-0 w-full h-1/3 bg-foreground"
                animate={{ y: [0, 36, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Marquee Strip */}
      <MarqueeStrip />

      {/* Services Section */}
      <section
        data-testid="services-section"
        className="py-20 md:py-32 lg:py-40"
      >
        <div className="container-wide">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-24"
          >
            <div>
              <span className="eyebrow mb-4 block">What we do</span>
              <h2 className="text-display font-['DM_Sans']">
                Selected services
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="view-all-services"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest border-b border-foreground/20 pb-1 hover:border-primary hover:text-primary transition-all duration-300"
            >
              View All
              <ArrowRight size={14} />
            </Link>
          </motion.div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {featuredServices.map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                index={index}
                variant="compact"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <div className="border-t border-border">
        <ProcessSection />
      </div>

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="py-20 md:py-32 lg:py-40 bg-foreground text-background"
      >
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.2em] font-bold text-background/50 mb-6 block"
            >
              Get started
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-5xl lg:text-6xl font-medium tracking-tight font-['DM_Sans'] mb-8"
            >
              Need signage that fits your space and budget?
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link
                to="/quote"
                data-testid="cta-quote-button"
                className="inline-flex items-center gap-3 rounded-full bg-background text-foreground px-10 py-5 text-sm uppercase tracking-wider font-semibold hover:bg-accent transition-colors duration-300"
              >
                Get Quick Quote
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
